<script setup>
import { Head, router } from '@inertiajs/vue3';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import InputField from '@js/components/fields/InputField.vue';
import PageTitle from '@js/components/titles/PageTitle.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSuccess } from '@js/utils/toast';
import { Form } from 'vee-validate';
import { reactive, ref } from 'vue';
import { object, ref as yupRef, string } from 'yup';

const { token, email } = defineProps({
    token: {
        type: String,
        required: true,
    },
    email: {
        type: String,
        required: true,
    },
});

const { route } = useRoute();

const isSubmitting = ref(false);

const form = reactive({
    password: '',
    passwordConfirmation: '',
});

const validationSchema = object({
    password: string().required('Le mot de passe requis'),
    passwordConfirmation: string()
        .required('La confirmation du mot de passe est requise')
        .oneOf([yupRef('password')], 'Les mots de passe ne correspondent pas'),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(route('front.postResetPassword'), {
            token: token,
            email: email,
            password: form.password,
            passwordConfirmation: form.passwordConfirmation,
        })
        .then((response) => {
            if (response.data.status === 'passwords.reset') {
                toastSuccess(
                    'Votre mot de passe a été réinitialisé avec succès.',
                );

                router.visit(route('front.login'));
            } else {
                toastError('Une erreur est survenue.');
            }

            router.visit(route('front.login'));
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);
        });
}
</script>

<template>
    <Head>
        <title>Réinitialiser le mot de passe - Datakepler</title>
    </Head>

    <div
        class="flex h-full flex-col items-center justify-center bg-gray-100 p-4"
    >
        <img
            src="/images/logo/logo.svg"
            alt="Datakepler"
            class="mb-6 w-48 md:w-60"
        />

        <Form
            v-slot="{ errors }"
            :validation-schema="validationSchema"
            class="w-full max-w-md rounded border border-gray-200 bg-white p-6"
            @submit="submit"
        >
            <PageTitle>Réinitialiser le mot de passe</PageTitle>

            <InputField
                v-model="form.password"
                label="Mot de passe"
                field-name="password"
                field-id="password"
                type="password"
                required
            />

            <InputField
                v-model="form.passwordConfirmation"
                label="Confirmation du mot de passe"
                field-name="passwordConfirmation"
                field-id="passwordConfirmation"
                type="password"
                required
            />

            <div class="mt-6">
                <ActionButton
                    submit
                    :disabled="Object.keys(errors).length > 0 || isSubmitting"
                    class="w-full"
                    size="large"
                    :loading="isSubmitting"
                >
                    Réinitialiser le mot de passe
                </ActionButton>
            </div>
        </Form>
    </div>
</template>
